import { useState } from 'react';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import type { FulfillmentOrderLine } from '@/infrastructure/FulfillmentAdminClient';

/** What one line is being put back: how many came back, and onto which shelf. */
export interface ReturnRestockLine {
  lineNo: number;
  qty: number;
  locationCode: string;
}

export interface ReturnRestockModalProps {
  lines: FulfillmentOrderLine[];
  /** The shelf each line already sat on, by line number; a line the ledger cannot place starts empty. */
  knownLocations: Map<number, string>;
  busy: boolean;
  onClose: () => void;
  onConfirm: (lines: ReturnRestockLine[], memo: string) => void;
}

const MEMO_PLACEHOLDER =
  'Return reason · condition (visible damage etc.) · notes — also recorded in the order’s Comments history and the inbound log';

/**
 * M3 — what the customer sent back going onto shelves [L-M3]. Every line starts at zero because the
 * box rarely holds the whole order back, and a quantity the operator did not type is one nobody
 * counted: zero means excluded. A quantity needs somewhere to land, and may never exceed what shipped.
 */
export function ReturnRestockModal({ lines, knownLocations, busy, onClose, onConfirm }: ReturnRestockModalProps) {
  const [quantities, setQuantities] = useState<Record<number, string>>(() =>
    Object.fromEntries(lines.map((line) => [line.lineNo, '0'])),
  );
  const [locations, setLocations] = useState<Record<number, string>>(() =>
    Object.fromEntries(lines.map((line) => [line.lineNo, knownLocations.get(line.lineNo) ?? ''])),
  );
  const [memo, setMemo] = useState('');
  const dialogRef = useModalDialog(onClose);

  const entered = lines
    .map((line) => ({
      line,
      qty: Number(quantities[line.lineNo] ?? '0'),
      locationCode: (locations[line.lineNo] ?? '').trim(),
    }))
    .filter((row) => Number.isInteger(row.qty) && row.qty > 0);
  const overRestocked = entered.filter((row) => row.qty > row.line.totalQuantity);
  const missingLocation = entered.some((row) => row.locationCode.length === 0);
  const blocked = busy || entered.length === 0 || missingLocation || overRestocked.length > 0;

  return (
    <dialog className="vo-modal" ref={dialogRef} aria-label="Restock Selected to Warehouse">
      <header>
        Warehouse Restock — {lines.length} item(s)
        <button type="button" className="x" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body">
        <div className="note">
          Confirm the products, quantities, and locations to restock. Products with existing stock get their current
          location auto-filled. Restock qty defaults to 0 — enter only the qty actually returned (0 = excluded).
        </div>
        <table className="tbl" aria-label="Lines to restock">
          <thead>
            <tr>
              <th>Product</th>
              <th>Ordered Qty</th>
              <th>Restock Qty</th>
              <th>Location</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.lineNo}>
                <td>
                  <b>{line.sku}</b> · {line.productNameKr ?? line.productName ?? '–'}
                </td>
                <td className="num">{line.totalQuantity}</td>
                <td>
                  <input
                    type="number"
                    min={0}
                    aria-label={`${line.sku} restock qty`}
                    value={quantities[line.lineNo] ?? '0'}
                    onChange={(event) => setQuantities((was) => ({ ...was, [line.lineNo]: event.target.value }))}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    aria-label={`${line.sku} location`}
                    className={(locations[line.lineNo] ?? '').trim().length === 0 ? 'loc-need' : undefined}
                    placeholder="required"
                    value={locations[line.lineNo] ?? ''}
                    onChange={(event) => setLocations((was) => ({ ...was, [line.lineNo]: event.target.value }))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {overRestocked.map((row) => (
          <div className="failbanner" key={row.line.lineNo}>
            {row.line.sku} — restock qty cannot exceed the ordered qty ({row.line.totalQuantity})
          </div>
        ))}
        <div className="note">
          Entering a qty requires a location; if qty &gt; 0 with no location, confirm stays disabled.
        </div>
        <label>
          <span>Memo (Optional)</span>
          <textarea
            aria-label="Memo (Optional)"
            rows={2}
            placeholder={MEMO_PLACEHOLDER}
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
          />
        </label>
        <div className="note">On completion, Current Stocks quantities and locations update automatically.</div>
      </div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-green btn-sm"
          disabled={blocked}
          onClick={() =>
            onConfirm(
              entered.map((row) => ({ lineNo: row.line.lineNo, qty: row.qty, locationCode: row.locationCode })),
              memo,
            )
          }
        >
          Confirm Restock ({entered.length})
        </button>
      </div>
    </dialog>
  );
}
